import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formats a Date into a human-readable relative time string.
 * e.g. "Just now", "5m ago", "3h ago", "2d ago", or a locale date.
 */
export function formatRelativeTime(date: Date | null): string {
  if (!date) return "";
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 30) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

/**
 * Formats a word count into a human-readable string.
 * e.g. 42 → "42", 5000 → "5.0k", 1500000 → "1.5M"
 */
export function formatWordCount(count: number): string {
  if (count < 1000) return count.toString();
  if (count < 1000000) return `${(count / 1000).toFixed(1)}k`;
  return `${(count / 1000000).toFixed(1)}M`;
}

/**
 * Generates a URL-safe slug from a title string.
 */
export function generateSlug(title: string): string {
  return title
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/[\s_]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Safely stringifies and parses a Tiptap JSON object to strip out ProseMirror prototype getters
 * that get mangled when crossing the Next.js React Server Component boundary.
 */
export function sanitizeTiptapJson(content: unknown): Record<string, unknown> {
  if (!content) return {};
  return JSON.parse(JSON.stringify(content));
}
